import React, { useState, useEffect } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import './SendMessage.css';

const SendMessage = () => {
    const [formData, setFormData] = useState({
        text: '',
        html: '',
        id: '',
        from: '',
        environment: 'dev',
        userDescription: '',
        problemClass: [],
        sourceEnvironment: 'any',
        debug: false
    });
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isHtmlMode, setIsHtmlMode] = useState(false);
    const [tagInput, setTagInput] = useState('');
    const [traceLog, setTraceLog] = useState('');
    const [isTracing, setIsTracing] = useState(false);
    const [traceInterval, setTraceInterval] = useState(null);
    const [socket, setSocket] = useState(null);

    useEffect(() => {
        // Initialize socket connection
        const newSocket = io('http://localhost:5000');
        setSocket(newSocket);

        return () => {
            newSocket.close();
        };
    }, []);

    useEffect(() => {
        if (!socket) return;

        // Listen for trace logs
        socket.on('trace_log', (data) => {
            setTraceLog(prev => prev + `\n[${new Date().toLocaleTimeString()}] ${JSON.stringify(data.log_data, null, 2)}`);
        });

        // Listen for trace status updates
        socket.on('trace_status', (data) => {
            setTraceLog(prev => prev + `\n[${new Date().toLocaleTimeString()}] ${data.message}`);
        });

        return () => {
            socket.off('trace_log');
            socket.off('trace_status');
        };
    }, [socket]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleEnvironmentChange = (e) => {
        setFormData(prev => ({
            ...prev,
            environment: e.target.value
        }));
    };

    const handleProblemClassChange = (e) => {
        const value = e.target.value;
        setTagInput(value);
    };

    const handleProblemClassKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const newTag = tagInput.trim();
            if (newTag && !formData.problemClass.includes(newTag)) {
                setFormData(prev => ({
                    ...prev,
                    problemClass: [...prev.problemClass, newTag]
                }));
                setTagInput('');
            }
        } else if (e.key === ',') {
            e.preventDefault();
            const newTag = tagInput.trim();
            if (newTag && !formData.problemClass.includes(newTag)) {
                setFormData(prev => ({
                    ...prev,
                    problemClass: [...prev.problemClass, newTag]
                }));
                setTagInput('');
            }
        } else if (e.key === 'Backspace' && !tagInput && formData.problemClass.length > 0) {
            setFormData(prev => ({
                ...prev,
                problemClass: prev.problemClass.slice(0, -1)
            }));
        }
    };

    const removeTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            problemClass: prev.problemClass.filter(tag => tag !== tagToRemove)
        }));
    };

    const handleFetchData = async () => {
        try {
            const response = await axios.get(`/api/message/${formData.id}`, {
                withCredentials: true
            });
            if (response.data.status === 'success') {
                setFormData(prev => ({
                    ...prev,
                    ...response.data.data
                }));
            }
        } catch (err) {
            setError('Failed to fetch message data');
        }
    };

    const handleSaveMessage = async () => {
        try {
            const response = await axios.post('/api/message/save', formData, {
                withCredentials: true
            });
            if (response.data.status === 'success') {
                setMessage('Message saved successfully!');
            }
        } catch (err) {
            setError('Failed to save message');
        }
    };

    const handleFetch = async () => {
        if (!formData.id) {
            setError('Please enter a message ID');
            return;
        }

        try {
            const response = await fetch('/api/fetch-message', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: formData.id,
                    environment: formData.environment
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to fetch message');
            }

            if (data.status === 'success' && data.data) {
                let messageData = data.data.data;
                console.log('Fetched data:', messageData);
                
                setFormData(prev => ({
                    ...prev,
                    text: messageData.text || '',
                    html: messageData.html || '',
                    from: messageData.sender?.[0]?.address || '',
                    id: messageData._id || formData.id
                }));
                setMessage('Message fetched successfully');
                setError(null);
            } else {
                throw new Error(data.message || 'Failed to fetch message');
            }
        } catch (error) {
            setError(error.message);
            setMessage(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setError('');

        try {
            const response = await axios.post('/api/send-message', formData, {
                withCredentials: true
            });

            if (response.data.status === 'success') {
                setMessage('Message sent successfully!');
                
                // Start tracing if debug is enabled
                if (formData.debug) {
                    startTracing(formData.id);
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error sending message');
        }
    };

    const startTracing = async (messageId) => {
        if (!socket) return;

        // Join the trace room
        socket.emit('join_trace_room', { message_id: messageId });
        
        setTraceLog(prev => prev + `\n[${new Date().toLocaleTimeString()}] Starting trace for message ID: ${messageId}`);
    };

    const handleClear = () => {
        setFormData({
            text: '',
            html: '',
            id: '',
            from: '',
            environment: 'dev',
            userDescription: '',
            problemClass: [],
            sourceEnvironment: 'any',
            debug: false
        });
        setTagInput('');
        setMessage('');
        setError('');
        setTraceLog('');
        if (traceInterval) {
            clearInterval(traceInterval);
            setTraceInterval(null);
        }
        setIsTracing(false);
        
        // Leave trace room if active
        if (socket && formData.id) {
            socket.emit('leave_trace_room', { message_id: formData.id });
        }
    };

    const handleIdChange = (e) => {
        const value = e.target.value;
        let extractedId = value;

        // Check if the value is a URL
        if (value.includes('anotatr.com')) {
            // Extract the last part of the URL
            const urlParts = value.split('/');
            const lastPart = urlParts[urlParts.length - 1];
            
            // Check if it's a UUID format
            const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            if (uuidRegex.test(lastPart)) {
                extractedId = UUIDToObjectId(lastPart);
            } else {
                // If it's a MongoDB ObjectId (24 characters)
                const objectIdRegex = /^[0-9a-fA-F]{24}$/;
                if (objectIdRegex.test(lastPart)) {
                    extractedId = lastPart;
                }
            }
        }

        setFormData(prev => ({
            ...prev,
            id: extractedId
        }));
    };

    const UUIDToObjectId = (uuid) => {
        const uidParts = uuid.split('-');
        const nsHex = uidParts[2].slice(1) + uidParts[1] + uidParts[0];
        const sHex = ((((parseInt(nsHex, 16) / 10000) - 12219292800000) / 1000) | 0).toString(16).padStart(8, '0');
        const correction = ((parseInt(uidParts[0].slice(6), 16) & 0x3) << 14);
        const counter = (parseInt(uidParts[3], 16) & 0x3fff) | correction;
        const nodeAndCounter = uidParts[4] + counter.toString(16).padStart(4, '0');
        return sHex + nodeAndCounter;
    };

    return (
        <div className="send-message-container">
            <h2>Send Message to Queue</h2>
            <div className="top-controls">
                <div className="control-group">
                    <label htmlFor="environment">Environment:</label>
                    <select
                        id="environment"
                        name="environment"
                        value={formData.environment}
                        onChange={handleChange}
                    >
                        <option value="dev">Dev</option>
                        <option value="prod">Prod</option>
                    </select>
                </div>

                <div className="control-group">
                    <label htmlFor="from">From:</label>
                    <input
                        type="text"
                        id="from"
                        name="from"
                        value={formData.from}
                        readOnly
                    />
                </div>

                <div className="control-group">
                    <label htmlFor="id">ID:</label>
                    <div className="id-input-group">
                        <input
                            type="text"
                            id="id"
                            name="id"
                            value={formData.id}
                            onChange={handleIdChange}
                            placeholder="Enter ID"
                        />
                        <button
                            type="button"
                            className="fetch-button"
                            onClick={handleFetch}
                        >
                            Fetch
                        </button>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="mode-tabs">
                    <div 
                        className={`tab ${!isHtmlMode ? 'active' : ''}`}
                        onClick={() => setIsHtmlMode(false)}
                    >
                        Text
                    </div>
                    <div 
                        className={`tab ${isHtmlMode ? 'active' : ''}`}
                        onClick={() => setIsHtmlMode(true)}
                    >
                        HTML
                    </div>
                </div>

                <div className="form-group">
                    {isHtmlMode ? (
                        <>
                            <label htmlFor="html">HTML:</label>
                            <div className="preview-container">
                                <div className="preview-frame">
                                    <iframe
                                        title="HTML Preview"
                                        srcDoc={formData.html}
                                        sandbox="allow-scripts"
                                        style={{
                                            width: '100%',
                                            height: '400px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px'
                                        }}
                                    />
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <label htmlFor="text">Text:</label>
                            <textarea
                                id="text"
                                name="text"
                                value={formData.text}
                                onChange={handleChange}
                                required
                            />
                        </>
                    )}
                </div>

                <div className="form-group">
                    <label htmlFor="userDescription">User Description:</label>
                    <textarea
                        id="userDescription"
                        name="userDescription"
                        value={formData.userDescription}
                        onChange={handleChange}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="traceLog">Trace Log:</label>
                    <textarea
                        id="traceLog"
                        value={traceLog}
                        readOnly
                        className="trace-log"
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="problemClass">Problem Class:</label>
                    <div className="tags-input-container">
                        <div className="tags-list">
                            {formData.problemClass.map((tag, index) => (
                                <span key={index} className="tag">
                                    {tag}
                                    <button
                                        type="button"
                                        className="tag-remove"
                                        onClick={() => removeTag(tag)}
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>
                        <input
                            type="text"
                            id="problemClass"
                            value={tagInput}
                            onChange={handleProblemClassChange}
                            onKeyDown={handleProblemClassKeyDown}
                            placeholder="Type and press Enter or comma to add"
                        />
                    </div>
                </div>

                <div className="button-group">
                    <button type="button" className="save-button" onClick={handleSaveMessage}>
                        Save Message
                    </button>
                    <button type="button" className="clear-button" onClick={handleClear}>
                        Clear
                    </button>
                    <div className="send-group">
                        <label className="test-trace-label">
                            <input
                                type="checkbox"
                                name="debug"
                                checked={formData.debug}
                                onChange={handleChange}
                            />
                            Test Trace
                        </label>
                        <button type="submit">
                            Send Message
                        </button>
                    </div>
                </div>
            </form>

            {message && <div className="success-message">{message}</div>}
            {error && <div className="error-message">{error}</div>}
        </div>
    );
};

export default SendMessage;